import { Add, ArrowBack, Check, Close, Delete, History, PrintOutlined } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    IconButton,
    InputLabel,
    ListSubheader,
    MenuItem,
    Select,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    ThemeProvider,
    Tooltip,
    Typography,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { HtmlSourcePanel } from './components/editor/HtmlSourcePanel';
import { LivePreviewPanel } from './components/editor/LivePreviewPanel';
import { PreviewResizeHandle } from './components/editor/PreviewResizeHandle';
import { PrintPreview } from './components/editor/PrintPreview';
import { RichTextEditor } from './components/editor/RichTextEditor';
import { VariablesStatusBar } from './components/editor/VariablesStatusBar';
import { VersionCompareModal } from './components/templates/VersionCompareModal';

import api from 'api';
import { AgentRoles } from 'api/models/payload/AgentRoles';
import { DEFAULT_BRANDING } from 'config/defaultBranding';
import { getMandatoryPlaceholdersForType } from 'config/placeholders';
import { useToast } from 'hooks/use-toast';
import {
    createTemplate,
    loadTemplate,
    loadTemplateVersions,
    updateTemplate,
} from 'redux/retainer-templates/templates.middleware';
import { clearCurrentTemplate } from 'redux/retainer-templates/templates.slice';
import type { AppDispatch, RootState } from 'redux/store';
import appThemeV2 from 'theme/themeV2';
import type {
    Agent,
    BrandingSettings,
    PlaceholderDefinition,
    PricingConfiguration,
    PricingModel,
    TemplateStatus,
    TemplateType,
} from 'types';
import { AgentSignature } from 'types/signature';
import { isFeatureNotEnabledError } from 'utils/features';

const MIN_PREVIEW_WIDTH = 320;
const MIN_EDITOR_WIDTH = 360;
const DEFAULT_PREVIEW_WIDTH = 540;

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <Box
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            sx={
                value === index
                    ? {
                          flex: 1,
                          m: 0,
                          minHeight: 0,
                          overflow: 'hidden',
                          display: 'flex',
                          flexDirection: 'column',
                      }
                    : undefined
            }
            {...other}
        >
            {value === index && children}
        </Box>
    );
}

function a11yProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

export default function TemplateEditorPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { toast } = useToast();
    const dispatch = useDispatch<AppDispatch>();
    const { currentTemplate, versions } = useSelector((state: RootState) => state.templates);
    const isEditMode = Boolean(id && id !== 'new');
    const editorRef = useRef<{
        insertPlaceholder: (token: string, mandatory: boolean) => void;
    } | null>(null);

    const [name, setName] = useState('');
    const [type, setType] = useState<TemplateType>('visa-application');
    const [country, setCountry] = useState('');
    const [program, setProgram] = useState('');
    const [description, setDescription] = useState('');
    const [defaultCurrency, setDefaultCurrency] = useState('CAD');
    const [defaultMilestones, setDefaultMilestones] = useState(false);
    const [pricingModel, setPricingModel] = useState<PricingModel>('milestone');
    const [status, setStatus] = useState<TemplateStatus>('draft');
    const [, setTags] = useState<string[]>([]);
    const [htmlContent, setHtmlContent] = useState('<p>Start typing your template here...</p>');
    const [branding, setBranding] = useState<BrandingSettings>(DEFAULT_BRANDING);
    const [pricingConfig, setPricingConfig] = useState<PricingConfiguration>({
        model: 'milestone',
        currency: 'CAD',
        milestone: {
            displayMode: 'amount',
            totalFee: 0,
            milestones: [],
        },
    });
    // Template state is now managed by Redux, keeping local state for form fields
    const [compareModalOpen, setCompareModalOpen] = useState(false);
    const [lastSaved, setLastSaved] = useState<Date | null>(null);
    const [savingAction, setSavingAction] = useState<'draft' | 'publish' | null>(null);
    const savingRef = useRef(false);
    const [tabValue, setTabValue] = useState(0);
    const [showPrintPreview, setShowPrintPreview] = useState(false);
    const [previewWidth, setPreviewWidth] = useState(DEFAULT_PREVIEW_WIDTH);
    const [missingPlaceholders, setMissingPlaceholders] = useState<PlaceholderDefinition[]>([]);
    const [foundPlaceholders, setFoundPlaceholders] = useState<string[]>([]);
    const foundPlaceholdersRef = useRef<string[]>([]);
    const previewLayoutRef = useRef<HTMLDivElement>(null);

    const getMaxPreviewWidth = useCallback(() => {
        const layoutWidth = previewLayoutRef.current?.clientWidth ?? window.innerWidth;
        return Math.max(MIN_PREVIEW_WIDTH, layoutWidth - MIN_EDITOR_WIDTH);
    }, []);

    // Dropdown states
    const [, setVisaAgents] = useState<{ label: string; value: string }[]>([]);
    const [agentsList, setAgentsList] = useState<Agent[]>([]);
    const [, setCountryBasedProgramList] = useState<{ label: string; value: string | number }[]>(
        []
    );
    const [selectedAgent, setSelectedAgent] = useState('');
    const [agentSignature, setAgentSignature] = useState<AgentSignature | null>(null);

    // Milestones dialog state
    const [milestonesDialogOpen, setMilestonesDialogOpen] = useState(false);
    const [milestones, setMilestones] = useState<
        Array<{ name: string; explanation: string; fee: number; currency: string }>
    >([]);
    const [currentMilestone, setCurrentMilestone] = useState({
        name: '',
        explanation: '',
        fee: 0,
        currency: 'USD',
    });

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const handleAddMilestone = () => {
        if (currentMilestone.name && currentMilestone.fee > 0) {
            setMilestones([...milestones, currentMilestone]);
            setCurrentMilestone({
                name: '',
                explanation: '',
                fee: 0,
                currency: currentMilestone.currency,
            });
        }
    };

    const handleDeleteMilestone = (index: number) => {
        setMilestones(milestones.filter((_, i) => i !== index));
    };

    const handleSaveMilestones = async () => {
        // Update pricing config with milestones
        const updatedPricingConfig = {
            ...pricingConfig,
            milestone: {
                milestones: milestones.map((m, index) => ({
                    id: `milestone-${Date.now()}-${index}`,
                    title: m.name,
                    description: m.explanation,
                    amount: m.fee,
                    triggerEvent: 'Manual',
                    dueDateType: 'relative' as const,
                    relativeDays: 0,
                    visible: true,
                    order: index,
                })),
                displayMode: 'amount' as const,
            },
        };
        setPricingConfig(updatedPricingConfig);

        // Close dialog and save template
        setMilestonesDialogOpen(false);

        toast({
            title: 'Milestones saved',
            description:
                'Milestones have been added to the template. Remember to save the template.',
        });
    };

    useEffect(() => {
        if (pricingModel !== pricingConfig.model) {
            const newConfig: PricingConfiguration = {
                model: pricingModel,
                currency: defaultCurrency,
            };

            switch (pricingModel) {
                case 'fixed':
                    newConfig.fixed = { baseFeeAmount: 0 };
                    break;
                case 'hourly':
                    newConfig.hourly = { hourlyRate: 0, estimatedHours: 0 };
                    break;
                case 'milestone':
                    newConfig.milestone = { milestones: [], displayMode: 'amount' };
                    break;
                case 'hybrid':
                    newConfig.hybrid = { upfrontFee: 0, milestones: [], displayMode: 'amount' };
                    break;
            }

            setPricingConfig(newConfig);
        }
    }, [pricingModel, defaultCurrency, pricingConfig.model]);

    useEffect(() => {
        setPricingConfig((prev: PricingConfiguration) => ({ ...prev, currency: defaultCurrency }));
    }, [defaultCurrency]);

    useEffect(() => {
        if (isEditMode && id) {
            dispatch(loadTemplate(Number(id)));
            dispatch(loadTemplateVersions(Number(id)));
        } else {
            dispatch(clearCurrentTemplate());
        }
    }, [id, isEditMode, dispatch]);

    useEffect(() => {
        if (currentTemplate && isEditMode && String(currentTemplate.id) === id) {
            setName(currentTemplate.name);
            setType(currentTemplate.type);
            setCountry(currentTemplate.country);
            setProgram(currentTemplate.program);
            setSelectedAgent(
                currentTemplate.assignedAgentId ? String(currentTemplate.assignedAgentId) : ''
            );
            setDescription(currentTemplate.description || '');
            setDefaultCurrency(currentTemplate.defaultCurrency || 'CAD');
            setDefaultMilestones(currentTemplate.defaultMilestones || false);
            setPricingModel(currentTemplate.pricingModel);
            setStatus(currentTemplate.status);
            setTags(currentTemplate.tags);
            setHtmlContent(
                currentTemplate.htmlContent || currentTemplate.content || '<p>Start typing...</p>'
            );
            setBranding(currentTemplate.branding || DEFAULT_BRANDING);

            if (currentTemplate.pricingConfig) {
                const pricingCfg = currentTemplate.pricingConfig;
                setPricingConfig(pricingCfg);

                // Populate milestones from API response
                if (pricingCfg.milestone?.milestones) {
                    const loadedMilestones = pricingCfg.milestone.milestones.map((m) => ({
                        name: m.title,
                        explanation: m.description,
                        fee: m.amount || 0,
                        currency: pricingCfg.currency || currentTemplate.defaultCurrency || 'USD',
                    }));
                    setMilestones(loadedMilestones);
                }
            }
        }
    }, [currentTemplate, isEditMode, id]);

    // Check for missing mandatory placeholders based on found placeholders from editor
    useEffect(() => {
        const missing: PlaceholderDefinition[] = [];
        const mandatoryPlaceholders = getMandatoryPlaceholdersForType(type);

        // Normalize found placeholders - they might come with or without braces
        const normalizedFound = foundPlaceholders.map((p) => {
            // If it already has braces, keep it; otherwise add braces
            return p.startsWith('{{') && p.endsWith('}}') ? p : `{{${p}}}`;
        });

        mandatoryPlaceholders.forEach((placeholder) => {
            // Check both with and without braces for flexibility
            const found =
                normalizedFound.includes(placeholder.token) ||
                foundPlaceholders.includes(placeholder.token) ||
                foundPlaceholders.includes(placeholder.token.replace(/[{}]/g, ''));

            if (!found) {
                missing.push(placeholder);
            }
        });

        console.log('[PlaceholderState] useEffect: Calculating missing placeholders', {
            foundPlaceholders: {
                count: foundPlaceholders.length,
                tokens: foundPlaceholders,
            },
            normalizedFound: {
                count: normalizedFound.length,
                tokens: normalizedFound,
            },
            mandatoryPlaceholders: {
                count: mandatoryPlaceholders.length,
                tokens: mandatoryPlaceholders.map((p) => p.token),
            },
            missingPlaceholders: {
                count: missing.length,
                placeholders: missing.map((p) => ({ token: p.token, label: p.label })),
            },
        });

        setMissingPlaceholders(missing);
    }, [foundPlaceholders, type]);

    // Strip any HTML tags that TipTap may have injected inside {{...}} tokens
    // e.g. {{party_<a href="...">a.org</a>_name}} → {{party_a.org_name}}
    const sanitizeTokensInHtml = (html: string): string => {
        return html.replace(/\{\{([^}]*)\}\}/g, (match) => {
            return match.replace(/<[^>]+>/g, '');
        });
    };

    const handleSave = async (publish: boolean = false) => {
        if (savingRef.current) return;

        if (!name.trim()) {
            toast({
                title: 'Validation Error',
                description: 'Template name is required.',
                variant: 'destructive',
            });
            return;
        }

        if (!htmlContent || htmlContent.trim() === '' || htmlContent === '<p></p>') {
            toast({
                title: 'Validation Error',
                description: 'Template content cannot be empty.',
                variant: 'destructive',
            });
            return;
        }

        // Check for mandatory placeholders with normalization
        const missing: PlaceholderDefinition[] = [];
        const mandatoryPlaceholders = getMandatoryPlaceholdersForType(type);

        // Normalize found placeholders - they might come with or without braces
        const normalizedFound = foundPlaceholders.map((p) => {
            // If it already has braces, keep it; otherwise add braces
            return p.startsWith('{{') && p.endsWith('}}') ? p : `{{${p}}}`;
        });

        mandatoryPlaceholders.forEach((placeholder) => {
            // Check both with and without braces for flexibility
            const found =
                normalizedFound.includes(placeholder.token) ||
                foundPlaceholders.includes(placeholder.token) ||
                foundPlaceholders.includes(placeholder.token.replace(/[{}]/g, ''));

            if (!found) {
                missing.push(placeholder);
            }
        });

        console.log('[PlaceholderState] handleSave: Checking mandatory placeholders', {
            foundPlaceholders: {
                count: foundPlaceholders.length,
                tokens: foundPlaceholders,
            },
            normalizedFound: {
                count: normalizedFound.length,
                tokens: normalizedFound,
            },
            missingPlaceholders: {
                count: missing.length,
                placeholders: missing.map((p) => ({ token: p.token, label: p.label })),
            },
        });

        if (missing.length > 0) {
            setMissingPlaceholders(missing);
            toast({
                title: 'Validation Error',
                description:
                    'Please include all mandatory variables in your template before saving.',
                variant: 'destructive',
            });
            return;
        }

        const finalStatus = publish ? 'published' : status;

        savingRef.current = true;
        setSavingAction(publish ? 'publish' : 'draft');
        try {
            if (isEditMode && id) {
                await dispatch(
                    updateTemplate({
                        id: Number(id),
                        updates: {
                            name,
                            type,
                            country,
                            program,
                            assignedAgentId: selectedAgent ? Number(selectedAgent) : null,
                            description,
                            defaultCurrency,
                            defaultMilestones,
                            pricingModel,
                            status: finalStatus,
                            // tags,
                            content: sanitizeTokensInHtml(htmlContent),
                            htmlContent: sanitizeTokensInHtml(htmlContent),
                            branding,
                            pricingConfig,
                            hasLogo: !!(branding.headerLogo || branding.footerLogo),
                        },
                    })
                ).unwrap();

                setLastSaved(new Date());
                toast({
                    title: publish ? 'Template published' : 'Template saved',
                    description: publish
                        ? 'Your template is now published and ready to use.'
                        : 'Your changes have been saved as a draft.',
                });
                if (publish) {
                    navigate('/templates');
                }
            } else {
                const response = await dispatch(
                    createTemplate({
                        name,
                        type,
                        country,
                        program,
                        assignedAgentId: selectedAgent ? Number(selectedAgent) : null,
                        description,
                        defaultCurrency,
                        defaultMilestones,
                        pricingModel,
                        status: finalStatus,
                        // tags,
                        content: sanitizeTokensInHtml(htmlContent),
                        htmlContent: sanitizeTokensInHtml(htmlContent),
                        branding,
                        pricingConfig,
                        hasLogo: !!(branding.headerLogo || branding.footerLogo),
                    })
                ).unwrap();

                setLastSaved(new Date());
                toast({
                    title: publish ? 'Template published' : 'Template created',
                    description: publish
                        ? 'Your template has been created and published.'
                        : 'Your template has been created as a draft.',
                });
                if (publish) {
                    navigate('/templates');
                } else if (response?.data?.data?.id) {
                    navigate(`/templates/${response.data.data.id}/edit`);
                }
            }
        } catch (error) {
            if (isFeatureNotEnabledError(error)) {
                return;
            }
            toast({
                title: 'Error',
                description: 'Failed to save template. Please try again.',
                variant: 'destructive',
            });
        } finally {
            savingRef.current = false;
            setSavingAction(null);
        }
    };

    const handleResetBranding = () => {
        if (window.confirm('Are you sure you want to reset all branding settings to defaults?')) {
            setBranding(DEFAULT_BRANDING);
            toast({
                title: 'Branding reset',
                description: 'All branding settings have been reset to defaults.',
            });
        }
    };

    // Memoize the callback to prevent infinite loops
    const handlePlaceholdersChange = useCallback((placeholders: string[]) => {
        const previousPlaceholders = foundPlaceholdersRef.current;

        // Only update if placeholders actually changed
        const hasChanged =
            previousPlaceholders.length !== placeholders.length ||
            !previousPlaceholders.every((p, i) => p === placeholders[i]);

        if (!hasChanged) {
            console.log(
                '[PlaceholderState] onPlaceholdersChange: No change detected, skipping update'
            );
            return;
        }

        console.log('[PlaceholderState] onPlaceholdersChange: Updating foundPlaceholders', {
            previous: {
                count: previousPlaceholders.length,
                tokens: previousPlaceholders,
            },
            new: {
                count: placeholders.length,
                tokens: placeholders,
            },
            changed: true,
        });

        foundPlaceholdersRef.current = placeholders;
        setFoundPlaceholders(placeholders);
    }, []);

    const handleInsertPlaceholder = (token: string, mandatory: boolean) => {
        if (editorRef.current) {
            editorRef.current.insertPlaceholder(token, mandatory);
        }
    };

    // Load programs based on selected country
    useEffect(() => {
        const getPrograms = async (country: string) => {
            try {
                console.log('📋 Loading programs for country:', country);
                const resp = await api.programList.get(1, country); // Using dummy user ID
                console.log('📋 Programs response:', resp);

                if (resp?.status === 200 || resp?.status === 201) {
                    const data = resp?.data;
                    if (Array.isArray(data) && data.length > 0) {
                        if (typeof data[0] === 'object' && data[0].program_name) {
                            const programList = data.map(
                                (e: { program_name: string; display_color?: string }) => ({
                                    value: e.program_name,
                                    label: e.program_name,
                                    display_color: e.display_color,
                                })
                            );
                            console.log('✅ Programs loaded (object format):', programList);
                            setCountryBasedProgramList(programList);
                        } else {
                            const programList = data.map((program: string) => ({
                                value: program,
                                label: program,
                                display_color: 'black',
                            }));
                            console.log('✅ Programs loaded (string format):', programList);
                            setCountryBasedProgramList(programList);
                        }
                    } else {
                        console.log('⚠️ No programs found');
                        setCountryBasedProgramList([]);
                    }
                }
            } catch (err) {
                console.error('❌ Failed to load programs:', err);
                setCountryBasedProgramList([]);
            }
        };

        if (country) {
            getPrograms(country);
        } else {
            setCountryBasedProgramList([]);
        }
    }, [country]);

    // Load visa agents on mount
    useEffect(() => {
        const getAgents = async () => {
            try {
                const resp = await api.agents.get(AgentRoles.CONSULTANT);
                const agents = resp?.data || [];
                setAgentsList(agents);
                setVisaAgents(
                    agents.map((e: Agent) => ({
                        label: [e.firstname, e.middlename, e.lastname].filter(Boolean).join(' '),
                        value: e.id.toString(),
                    }))
                );
            } catch (e) {
                console.error('Failed to load agents:', e);
                setAgentsList([]);
                setVisaAgents([]);
            }
        };

        getAgents();
    }, []);

    // Fetch agent signature when agent is selected
    useEffect(() => {
        const fetchAgentSignature = async () => {
            if (!selectedAgent) {
                setAgentSignature(null);
                return;
            }

            try {
                const resp = await api.signatures.getAgentDefaultSignature(Number(selectedAgent));
                if (resp?.data) {
                    setAgentSignature(resp.data);
                } else {
                    setAgentSignature(null);
                }
            } catch (e) {
                console.error('Failed to load agent signature:', e);
                setAgentSignature(null);
            }
        };

        fetchAgentSignature();
    }, [selectedAgent]);

    return (
        <ThemeProvider theme={appThemeV2}>
            <>
                <Box
                    sx={{
                        boxSizing: 'border-box',
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100dvh',
                        overflowX: 'hidden',
                        bgcolor: '#f8f9fa',
                        pt: 7,
                    }}
                >
                    <Stack
                        spacing={1.25}
                        sx={{
                            bgcolor: 'background.paper',
                            borderBottom: 1,
                            borderColor: 'divider',
                            px: 3,
                            py: 1.5,
                        }}
                    >
                        <Stack
                            direction="row"
                            spacing={1.5}
                            sx={{ alignItems: 'center', minWidth: 0 }}
                        >
                            <Tooltip title="Back to templates">
                                <IconButton
                                    size="small"
                                    onClick={() => navigate(-1)}
                                    aria-label="Back to templates"
                                    sx={{
                                        border: 1,
                                        borderColor: 'divider',
                                        borderRadius: 1,
                                        color: 'text.primary',
                                        '&:hover': {
                                            borderColor: 'primary.main',
                                            color: 'primary.main',
                                        },
                                    }}
                                >
                                    <ArrowBack sx={{ fontSize: 18 }} />
                                </IconButton>
                            </Tooltip>

                            <TextField
                                value={name}
                                onChange={(event) => setName(event.target.value)}
                                placeholder="Untitled template"
                                variant="standard"
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    '& .MuiInputBase-input': {
                                        fontSize: 18,
                                        fontWeight: 700,
                                        color: 'text.primary',
                                        py: 0.25,
                                        '&::placeholder': { color: 'error.main', opacity: 1 },
                                    },
                                    '& .MuiInput-underline:before': {
                                        borderBottomColor: 'transparent',
                                    },
                                    '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                                        borderBottomColor: 'transparent',
                                    },
                                    '& .MuiInput-underline:after': {
                                        borderBottomColor: 'primary.main',
                                    },
                                }}
                            />

                            <Chip
                                label={isEditMode ? 'Editing' : 'New'}
                                size="small"
                                sx={{
                                    fontWeight: 600,
                                    fontSize: 11,
                                    backgroundColor: isEditMode ? '#ede9fe' : '#dcfce7',
                                    color: isEditMode ? '#6d28d9' : '#166534',
                                    borderRadius: 1,
                                }}
                            />

                            {lastSaved && (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ whiteSpace: 'nowrap' }}
                                >
                                    Saved{' '}
                                    {lastSaved.toLocaleTimeString([], {
                                        hour: '2-digit',
                                        minute: '2-digit',
                                    })}
                                </Typography>
                            )}

                            <Divider
                                orientation="vertical"
                                flexItem
                                sx={{ alignSelf: 'center', height: 24 }}
                            />

                            {isEditMode && currentTemplate && versions.length > 1 && (
                                <Tooltip title="Compare versions">
                                    <IconButton
                                        size="small"
                                        onClick={() => setCompareModalOpen(true)}
                                        aria-label="Compare versions"
                                        sx={{
                                            border: 1,
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                            color: 'primary.main',
                                            '&:hover': { bgcolor: 'action.hover' },
                                        }}
                                    >
                                        <History sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </Tooltip>
                            )}

                            <Button
                                variant="outlined"
                                color="inherit"
                                disabled={savingAction !== null}
                                onClick={() => handleSave(false)}
                                startIcon={
                                    savingAction === 'draft' ? (
                                        <CircularProgress size={14} color="primary" />
                                    ) : undefined
                                }
                                sx={{
                                    height: 36,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: 13,
                                    whiteSpace: 'nowrap',
                                    color: 'text.primary',
                                    borderColor: 'divider',
                                    opacity:
                                        savingAction !== null && savingAction !== 'draft' ? 0.6 : 1,
                                    '&:hover': {
                                        borderColor: 'primary.main',
                                        color: 'primary.main',
                                    },
                                }}
                            >
                                Save Draft
                            </Button>
                            <Button
                                variant="contained"
                                disabled={savingAction !== null}
                                onClick={() => handleSave(true)}
                                startIcon={
                                    savingAction === 'publish' ? (
                                        <CircularProgress size={14} sx={{ color: '#fff' }} />
                                    ) : (
                                        <Check sx={{ fontSize: 16 }} />
                                    )
                                }
                                sx={{
                                    height: 36,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: 13,
                                    whiteSpace: 'nowrap',
                                    color: '#fff',
                                    boxShadow: 'none',
                                    opacity:
                                        savingAction !== null && savingAction !== 'publish'
                                            ? 0.6
                                            : 1,
                                }}
                            >
                                Publish
                            </Button>
                        </Stack>

                        <Stack
                            direction="row"
                            spacing={2}
                            sx={{ alignItems: 'flex-start', flexWrap: 'wrap' }}
                        >
                            {/* Type selector */}
                            <FormControl size="small" sx={{ minWidth: 240, flexShrink: 0 }}>
                                <InputLabel sx={{ fontSize: 13 }}>Template Type</InputLabel>
                                <Select
                                    value={type}
                                    label="Template Type"
                                    onChange={(e) => setType(e.target.value as TemplateType)}
                                    sx={{
                                        fontSize: 13,
                                        fontWeight: 600,
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#8353E2',
                                            borderWidth: 1.5,
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#6025D8',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#6025D8',
                                        },
                                        color: '#6025D8',
                                        background: '#faf5ff',
                                    }}
                                    renderValue={(val) => {
                                        const isB2B = ![
                                            'visa-application',
                                            'service-agreement',
                                            'eligibility',
                                            'milestones',
                                            'engagement',
                                        ].includes(val);
                                        const labels: Record<string, string> = {
                                            'visa-application': 'Visa Application',
                                            'service-agreement': 'Service Agreement',
                                            eligibility: 'Eligibility Assessment',
                                            milestones: 'Milestones',
                                            engagement: 'Engagement Letter',
                                            referral: 'Referral Agreement',
                                            subcontractor: 'Sub-contractor Agreement',
                                            msa: 'Master Service Agreement',
                                            'revenue-sharing': 'Revenue Sharing',
                                            nda: 'NDA / Confidentiality',
                                            'preferred-partner': 'Preferred Partner',
                                        };
                                        return (
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                sx={{ alignItems: 'center' }}
                                            >
                                                <Chip
                                                    size="small"
                                                    label={isB2B ? 'B2B' : 'Individual'}
                                                    sx={{
                                                        height: 18,
                                                        fontSize: 10,
                                                        fontWeight: 700,
                                                        letterSpacing: 0.5,
                                                        bgcolor: isB2B ? '#ede9fe' : '#dbeafe',
                                                        color: isB2B ? '#6d28d9' : '#1d4ed8',
                                                    }}
                                                />
                                                {labels[val]}
                                            </Stack>
                                        );
                                    }}
                                >
                                    <ListSubheader
                                        sx={{
                                            fontSize: 11,
                                            fontWeight: 700,
                                            color: '#9ca3af',
                                            letterSpacing: 0.8,
                                            lineHeight: '32px',
                                            background: '#fff',
                                        }}
                                    >
                                        INDIVIDUAL CLIENT
                                    </ListSubheader>
                                    <MenuItem value="visa-application" sx={{ fontSize: 13 }}>
                                        Visa Application
                                    </MenuItem>
                                    <MenuItem value="service-agreement" sx={{ fontSize: 13 }}>
                                        Service Agreement
                                    </MenuItem>
                                    <MenuItem value="eligibility" sx={{ fontSize: 13 }}>
                                        Eligibility Assessment
                                    </MenuItem>
                                    <MenuItem value="milestones" sx={{ fontSize: 13 }}>
                                        Milestones
                                    </MenuItem>
                                    <MenuItem value="engagement" sx={{ fontSize: 13 }}>
                                        Engagement Letter
                                    </MenuItem>
                                    <Divider sx={{ my: 0.5 }} />
                                    <ListSubheader
                                        sx={{
                                            fontSize: 11,
                                            fontWeight: 700,
                                            color: '#9ca3af',
                                            letterSpacing: 0.8,
                                            lineHeight: '32px',
                                            background: '#fff',
                                        }}
                                    >
                                        B2B / ORG-TO-ORG
                                    </ListSubheader>
                                    <MenuItem value="referral" sx={{ fontSize: 13 }}>
                                        Referral Agreement
                                    </MenuItem>
                                    <MenuItem value="subcontractor" sx={{ fontSize: 13 }}>
                                        Sub-contractor Agreement
                                    </MenuItem>
                                    <MenuItem value="msa" sx={{ fontSize: 13 }}>
                                        Master Service Agreement (MSA)
                                    </MenuItem>
                                    <MenuItem value="revenue-sharing" sx={{ fontSize: 13 }}>
                                        Revenue Sharing Agreement
                                    </MenuItem>
                                    <MenuItem value="nda" sx={{ fontSize: 13 }}>
                                        NDA / Confidentiality
                                    </MenuItem>
                                    <MenuItem value="preferred-partner" sx={{ fontSize: 13 }}>
                                        Preferred Partner Agreement
                                    </MenuItem>
                                </Select>
                            </FormControl>

                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <VariablesStatusBar
                                    htmlContent={htmlContent}
                                    onInsert={handleInsertPlaceholder}
                                    foundPlaceholders={foundPlaceholders}
                                    templateType={type}
                                />
                            </Box>
                        </Stack>

                        {(missingPlaceholders.length > 0 || !name.trim()) && (
                            <Alert
                                severity="error"
                                icon={false}
                                sx={{ py: 0.5, alignItems: 'center' }}
                            >
                                <Stack
                                    direction="row"
                                    spacing={0.75}
                                    sx={{ flexWrap: 'wrap', alignItems: 'center' }}
                                >
                                    <Typography variant="caption" sx={{ fontWeight: 700, mr: 0.5 }}>
                                        Missing:
                                    </Typography>
                                    {!name.trim() && (
                                        <Chip
                                            size="small"
                                            color="error"
                                            variant="outlined"
                                            label="Template name"
                                        />
                                    )}
                                    {missingPlaceholders.map((placeholder) => (
                                        <Tooltip
                                            key={placeholder.token}
                                            title={placeholder.token}
                                            arrow
                                        >
                                            <Chip
                                                size="small"
                                                color="error"
                                                variant="outlined"
                                                label={placeholder.label}
                                            />
                                        </Tooltip>
                                    ))}
                                </Stack>
                            </Alert>
                        )}
                    </Stack>

                    <Box
                        ref={previewLayoutRef}
                        sx={{ flex: 1, display: 'flex', overflow: 'hidden', minWidth: 0 }}
                    >
                        <Box
                            sx={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden',
                                minWidth: 0,
                            }}
                        >
                            <RichTextEditor
                                content={htmlContent}
                                onChange={(newContent) => {
                                    setHtmlContent(newContent);
                                }}
                                editable={true}
                                pricingConfig={pricingConfig}
                                branding={branding}
                                onBrandingChange={setBranding}
                                onBrandingReset={handleResetBranding}
                                onPlaceholdersChange={handlePlaceholdersChange}
                                templateType={type}
                                showPrintPreviewButton={false}
                            />
                        </Box>

                        <Box
                            sx={{
                                width: previewWidth,
                                flexShrink: 0,
                                position: 'relative',
                                borderLeft: 1,
                                borderColor: 'divider',
                                bgcolor: 'grey.50',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <PreviewResizeHandle
                                currentWidth={previewWidth}
                                minWidth={MIN_PREVIEW_WIDTH}
                                getMaxWidth={getMaxPreviewWidth}
                                onWidthChange={setPreviewWidth}
                            />
                            <Stack
                                direction="row"
                                sx={{
                                    alignItems: 'center',
                                    bgcolor: 'background.paper',
                                    borderBottom: 1,
                                    borderColor: 'divider',
                                }}
                            >
                                <Tabs
                                    value={tabValue}
                                    onChange={handleTabChange}
                                    sx={{
                                        flex: 1,
                                        minWidth: 0,
                                        '& .MuiTabs-flexContainer': {
                                            justifyContent: 'flex-start',
                                        },
                                        '& .MuiTab-root': {
                                            textTransform: 'none',
                                            minHeight: 48,
                                        },
                                    }}
                                >
                                    <Tab label="Preview" {...a11yProps(0)} />
                                    <Tab label="HTML Source" {...a11yProps(1)} />
                                </Tabs>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => setShowPrintPreview(true)}
                                    startIcon={<PrintOutlined sx={{ fontSize: 16 }} />}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        mr: 1.5,
                                        flexShrink: 0,
                                    }}
                                >
                                    Print Preview
                                </Button>
                            </Stack>

                            <TabPanel value={tabValue} index={0}>
                                <LivePreviewPanel
                                    htmlContent={htmlContent}
                                    branding={branding}
                                    pricingConfig={pricingConfig}
                                    country={country}
                                    program={program}
                                    agentData={agentsList.find(
                                        (agent) => agent.id.toString() === selectedAgent
                                    )}
                                    agentSignature={agentSignature}
                                    templateType={type}
                                />
                            </TabPanel>

                            <TabPanel value={tabValue} index={1}>
                                <HtmlSourcePanel htmlContent={htmlContent} />
                            </TabPanel>
                        </Box>
                    </Box>
                </Box>

                <PrintPreview
                    open={showPrintPreview}
                    onOpenChange={setShowPrintPreview}
                    content={htmlContent}
                    branding={branding}
                />

                {currentTemplate && (
                    <VersionCompareModal
                        template={{ ...currentTemplate, versions }}
                        open={compareModalOpen}
                        onOpenChange={setCompareModalOpen}
                    />
                )}

                {/* Milestones Dialog */}
                <Dialog
                    open={milestonesDialogOpen}
                    onClose={() => setMilestonesDialogOpen(false)}
                    maxWidth="lg"
                    fullWidth
                >
                    <DialogTitle>
                        <Stack
                            direction="row"
                            sx={{ alignItems: 'center', justifyContent: 'space-between' }}
                        >
                            <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
                                Create Milestones
                            </Typography>
                            <IconButton
                                onClick={() => setMilestonesDialogOpen(false)}
                                size="small"
                                aria-label="Close"
                            >
                                <Close />
                            </IconButton>
                        </Stack>
                    </DialogTitle>
                    <DialogContent dividers>
                        <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            spacing={2}
                            sx={{ alignItems: { md: 'center' }, mb: 3 }}
                        >
                            <TextField
                                placeholder="First Milestone Name"
                                value={currentMilestone.name}
                                onChange={(event) =>
                                    setCurrentMilestone({
                                        ...currentMilestone,
                                        name: event.target.value,
                                    })
                                }
                                sx={{ flex: { md: '0 0 280px' }, width: { xs: '100%', md: 280 } }}
                            />
                            <TextField
                                placeholder="Milestone Explanation"
                                value={currentMilestone.explanation}
                                onChange={(event) =>
                                    setCurrentMilestone({
                                        ...currentMilestone,
                                        explanation: event.target.value,
                                    })
                                }
                                sx={{ flex: 1, width: { xs: '100%', md: 'auto' } }}
                            />
                            <Select
                                value={currentMilestone.currency}
                                onChange={(event) =>
                                    setCurrentMilestone({
                                        ...currentMilestone,
                                        currency: event.target.value,
                                    })
                                }
                                sx={{ width: { xs: '100%', md: 120 } }}
                            >
                                <MenuItem value="USD">USD</MenuItem>
                                <MenuItem value="CAD">CAD</MenuItem>
                                <MenuItem value="AUD">AUD</MenuItem>
                                <MenuItem value="GBP">GBP</MenuItem>
                                <MenuItem value="EUR">EUR</MenuItem>
                            </Select>
                            <TextField
                                placeholder="Fee"
                                type="number"
                                value={currentMilestone.fee || ''}
                                onChange={(event) =>
                                    setCurrentMilestone({
                                        ...currentMilestone,
                                        fee: parseFloat(event.target.value) || 0,
                                    })
                                }
                                sx={{ width: { xs: '100%', md: 120 } }}
                            />
                            <IconButton
                                onClick={handleAddMilestone}
                                aria-label="Add milestone"
                                disabled={!currentMilestone.name || currentMilestone.fee <= 0}
                                sx={{
                                    bgcolor: 'primary.main',
                                    color: 'common.white',
                                    width: 40,
                                    height: 38,
                                    '&:hover': { bgcolor: 'primary.dark' },
                                    '&.Mui-disabled': {
                                        bgcolor: 'action.disabledBackground',
                                        color: 'action.disabled',
                                    },
                                }}
                            >
                                <Add />
                            </IconButton>
                        </Stack>

                        {milestones.length > 0 && (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>
                                                Milestone Name
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>
                                                Explanation
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                Currency
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                Fee
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                sx={{ fontWeight: 600, width: 80 }}
                                            >
                                                Action
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {milestones.map((milestone, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{milestone.name}</TableCell>
                                                <TableCell>{milestone.explanation}</TableCell>
                                                <TableCell align="right">
                                                    {milestone.currency}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {milestone.fee.toFixed(2)}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <IconButton
                                                        onClick={() => handleDeleteMilestone(index)}
                                                        size="small"
                                                        aria-label="Delete milestone"
                                                        sx={{ color: 'error.main' }}
                                                    >
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ px: 3, py: 2 }}>
                        <Button
                            variant="outlined"
                            onClick={() => setMilestonesDialogOpen(false)}
                            sx={{ textTransform: 'none' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSaveMilestones}
                            disabled={milestones.length === 0}
                            sx={{ textTransform: 'none' }}
                        >
                            Save Milestones
                        </Button>
                    </DialogActions>
                </Dialog>
            </>
        </ThemeProvider>
    );
}
